import { useCallback, useMemo } from 'react';
import type { DateView } from '@src/domains/scheduling/hooks/types';

export type FormatLabel = (date: Date, view: DateView) => string;

type Props = {
  currentMonth: Date;
  view: DateView;
  views: readonly DateView[];
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  onMonthChange: (month: Date, direction: 'left' | 'right') => void;
  onViewChange?: (view: DateView) => void;
  formatLabel?: FormatLabel;
};

type Return = {
  label: string;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  labelInteractive: boolean;
  handlePrev: () => void;
  handleNext: () => void;
  handleLabelClick: () => void;
};

const addMonths = (date: Date, delta: number) => {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + delta);
  return next;
};

const addYears = (date: Date, delta: number) => {
  const next = new Date(date);
  next.setDate(1);
  next.setFullYear(next.getFullYear() + delta);
  return next;
};

const defaultFormatLabel: FormatLabel = (date, view) => {
  if (view === 'year' || view === 'month') return `${date.getFullYear()}`;
  const month = date.toLocaleDateString('pt-BR', { month: 'long' });
  const year = date.getFullYear();
  return `${month.charAt(0).toUpperCase()}${month.slice(1)} - ${year}`;
};

const nextView: Record<DateView, DateView> = {
  day: 'month',
  month: 'year',
  year: 'day',
};

export function useMonthNavigation(props: Props): Return {
  const {
    currentMonth,
    view,
    views,
    minDate,
    maxDate,
    disabled = false,
    onMonthChange,
    onViewChange,
    formatLabel = defaultFormatLabel,
  } = props;

  const stepFn = view === 'day' ? addMonths : addYears;
  const prev = useMemo(() => stepFn(currentMonth, -1), [stepFn, currentMonth]);
  const next = useMemo(() => stepFn(currentMonth, 1), [stepFn, currentMonth]);

  const isPrevDisabled = disabled || (minDate ? prev.getTime() < minDate.getTime() : false);
  const isNextDisabled = disabled || (maxDate ? next.getTime() > maxDate.getTime() : false);

  const handlePrev = useCallback(() => onMonthChange(prev, 'right'), [onMonthChange, prev]);
  const handleNext = useCallback(() => onMonthChange(next, 'left'), [onMonthChange, next]);

  const target = nextView[view];
  const labelInteractive = views.includes(target) && Boolean(onViewChange);

  const handleLabelClick = useCallback(() => {
    if (!labelInteractive || !onViewChange) return;
    onViewChange(target);
  }, [labelInteractive, onViewChange, target]);

  const label = useMemo(() => formatLabel(currentMonth, view), [formatLabel, currentMonth, view]);

  return { label, isPrevDisabled, isNextDisabled, labelInteractive, handlePrev, handleNext, handleLabelClick };
}
